import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {RootState} from "../../store/rootReducer";
import {ContextualMenuItemType, DefaultButton, IContextualMenuItem, IContextualMenuProps, useTheme} from "@fluentui/react";
import {INormalizedProduct} from "../../models/NormalizedProduct";
import {useGetRecentWorkspaceRefsQuery} from "../../store/Api";
import {useAddProductToWorkspaceMutation} from "../../store/endpoints/WorkspaceItemsEndpoints";
import {IWorkspaceReference} from "../../models/Workspace";
import {IProductBundleRef} from "../../models/ProductBundles";
import {
    useAddItemToProductBundleMutation,
    useGetTopProductBundleRefsQuery
} from "../../store/endpoints/ProductBundleEndpoints";

interface IAddToWorkspaceButtonProps {
    onChange?: () => void;
    product: INormalizedProduct;
    source?: string;
    priceLevel?: string;
    isExample?: boolean;
    selectedBundle?: IProductBundleRef;
    bundleKeywords?: string;
}


const AddToCollectionButton: React.FC<IAddToWorkspaceButtonProps> = (props) => {
    const theme = useTheme();
    const [addedWorkspaces, setAddedWorkspaces] = useState<string[]>([])
    const [showStatus, setShowStatus] = useState(false); // status indicator state
    const [updateSuccess, setUpdateSuccess] = useState<boolean>(false);
    const getWorkspaces = useGetRecentWorkspaceRefsQuery();
    const [addProduct] = useAddProductToWorkspaceMutation();
    const defaultWorkspace = useSelector((state: RootState) => state.settings.selectedWorkspaceId);
    const [selectedWorkspace, setSelectedWorkspace] = useState<IWorkspaceReference>()
    const desiredQuantity = useSelector((state: RootState) => state.search.desiredQuantity);
    const listedInStorefront = useSelector((state: RootState) => state.search.criteria.inStorefront);
    const storefronts = useSelector((state: RootState) => state.search.criteria.storefrontFilter);
    const getTopBundles = useGetTopProductBundleRefsQuery({listedInStorefront: listedInStorefront, keywords: props.bundleKeywords, storefronts: storefronts});
    const [addToBundle] = useAddItemToProductBundleMutation();
    
    useEffect(() => {
        if(props.isExample){
            setSelectedWorkspace({name: 'Demo Workspace', id: 'demo'})
        }
        else {
            if (!getWorkspaces.data || getWorkspaces.data.length === 0) return;
            const filteredWorkspaces = getWorkspaces.data.filter(a => a.active)
            if (defaultWorkspace) {
                const foundWorkspace = filteredWorkspaces.filter(a => a.id === defaultWorkspace)?.[0]
                if (foundWorkspace) {
                    setSelectedWorkspace(foundWorkspace)
                    return
                }
            }
            setSelectedWorkspace(filteredWorkspaces[0])
        }
    }, [getWorkspaces, defaultWorkspace]);
    
    function addToWorkspace(workspace: string) {
        if(props.isExample) return;

        addProduct({
            workspace,
            product: props.product,
            req: { source: props.source, priceLevelPartNumber: props.priceLevel, quantity: desiredQuantity }
        }).unwrap().then((result) => {
            if(result.success){
                setUpdateSuccess(true);
            }
            else{
                setUpdateSuccess(false)
            }
            setShowStatus(true); // show status indicator
            setTimeout(() => setShowStatus(false), 5000); // hide after 5s
        })
        const newAddedList = [workspace, ...addedWorkspaces];
        setAddedWorkspaces(newAddedList);
        if(props.onChange) {
            props.onChange();
        }
        
    }

    const getMenuProps = (): IContextualMenuItem[] => {
        // Example bundles, replace with your actual bundle data
        const workspaceItems: IContextualMenuItem[] = props.isExample
            ? [{ key: 'demo', text: 'Demo Workspace' }]
            : (getWorkspaces.data?.filter(a => a.active) ?? []).map(ws => ({
                disabled: addedWorkspaces.includes(ws.id),
                key: ws.id,
                text: ws?.name == undefined ? `Loading...` : `Add to ${ws.name} workspace`,
                onClick: () => addToWorkspace(ws.id)
            }));

        const bundleItems: IContextualMenuItem[] = (getTopBundles.data?.items ?? []).map(bundle => ({
            key: bundle.id ?? '',
            text: `Add to ${bundle.name} bundle`,
            onClick: () => {
                if(bundle.id && props.product.id && props.product.catalog) {
                    addToBundle({bundleId: bundle.id, catalog: props.product.catalog, productId: props.product.id}).unwrap().then((result) => {
                        if(result.success){
                            setUpdateSuccess(true);
                        }
                        else{
                            setUpdateSuccess(false)
                        }
                        setShowStatus(true); // show status indicator
                        setTimeout(() => setShowStatus(false), 5000); // hide after 5s
                    });
                }
            }
        }));
        
        if(props.selectedBundle){
            const check = bundleItems.findIndex(b => b.key === props.selectedBundle?.id)
            if(check !== -1){
                bundleItems.splice(check, 1)
            }
            bundleItems.unshift({
                key: props.selectedBundle.id ?? '',
                text: `Add to ${props.selectedBundle.name} bundle`,
                iconProps: { iconName: 'RadioBtnOn' },
                onClick: () => {
                    if(props.selectedBundle?.id && props.product.id && props.product.catalog) {
                        addToBundle({bundleId: props.selectedBundle.id, catalog: props.product.catalog, productId: props.product.id}).unwrap().then((result) => {
                            if(result.success){
                                setUpdateSuccess(true);
                            }
                            else{
                                setUpdateSuccess(false)
                            }
                            setShowStatus(true); // show status indicator
                            setTimeout(() => setShowStatus(false), 5000); // hide after 5s
                        })
                    }
                }
            });
            if(bundleItems.length > 5){
                bundleItems.splice(6, bundleItems.length - 6)
            }
        }
        
        if(!bundleItems || bundleItems.length === 0) {
            return [
                { key: 'workspacesHeader', itemType: ContextualMenuItemType.Header, text: 'Workspaces' },
                ...workspaceItems
            ];
        }
        
        return [
            { key: 'workspacesHeader', itemType: ContextualMenuItemType.Header, text: 'Workspaces' },
            ...workspaceItems,
            { key: 'bundlesHeader', itemType: ContextualMenuItemType.Header, text: 'Bundles' },
            ...bundleItems
        ];
    };

    const menuProps: IContextualMenuProps = {
        items: getMenuProps()
    }

    return ( 
        <div id={'addToWorkspace-container'} style={{display: 'flex', flexDirection: 'column', position: 'relative', alignItems: 'flex-start'}}>
            {props.onChange ?
            <DefaultButton
                text={ selectedWorkspace?.name == undefined ? `Loading...` : `Add to ${selectedWorkspace.name} workspace`}
                menuProps={menuProps}
                primary
                onClick={() => {
                    if (selectedWorkspace) {
                        addToWorkspace(selectedWorkspace.id)
                    }
                }}
                disabled={getWorkspaces.isLoading}
                primaryDisabled={!selectedWorkspace}
                split
                iconProps={{ iconName: 'Add' }}
            />
            : <DefaultButton
                text={'Add'}
                menuProps={menuProps}
                disabled={getWorkspaces.isFetching || getWorkspaces.isLoading || getTopBundles.isFetching || getTopBundles.isLoading}
                iconProps={{ iconName: 'Add' }}
                // Grid variant: sits flat on the row (transparent, green text) and only grows a green
                // border on hover, so a column of Add buttons does not compete with the figures.
                styles={{
                    // Only the plus is green; the label and chevron read as plain text.
                    root: {height: 36, padding: '0 6px', background: 'transparent', border: `1px solid transparent`, color: theme.palette.neutralPrimary},
                    rootHovered: {background: 'transparent', borderColor: theme.palette.themePrimary, color: theme.palette.neutralPrimary},
                    rootPressed: {background: theme.palette.themeLighter, borderColor: theme.palette.themePrimary, color: theme.palette.neutralPrimary},
                    rootExpanded: {background: theme.palette.themeLighter, borderColor: theme.palette.themePrimary, color: theme.palette.neutralPrimary},
                    rootDisabled: {background: 'transparent', borderColor: 'transparent'},
                    icon: {color: theme.palette.themePrimary},
                    iconHovered: {color: theme.palette.themeDark},
                    iconPressed: {color: theme.palette.themeDark},
                    menuIcon: {color: theme.palette.neutralPrimary},
                    menuIconHovered: {color: theme.palette.neutralPrimary},
                    menuIconExpanded: {color: theme.palette.neutralPrimary},
                    label: {color: 'inherit'},
                }}
            />}
            {showStatus && (
                <React.Fragment>
                {updateSuccess ? 
                <div style={{
                    width: '100px',
                    display: 'inline-flex',
                    background: '#e6ffe6',
                    color: '#107c10',
                    borderRadius: '12px',
                    padding: '2px 10px',
                    fontSize: '0.9em',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                    alignItems: 'center',
                    zIndex: 10,
                    marginTop: '8px'
                }}>
                    <span style={{marginRight: 4}}>&#10003;</span> Added!
                </div> :
                    <div style={{
                        width: '100px',
                        display: 'inline-flex',
                        background: '#cb4040',
                        color: '#fff2f2',
                        borderRadius: '12px',
                        padding: '2px 10px',
                        fontSize: '0.9em',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                        alignItems: 'center',
                        zIndex: 10,
                        marginTop: '8px'
                    }}>
                        <span style={{marginRight: 4}}>x</span> Failed to Add
                </div>}
                </React.Fragment>
            )}
        </div>
    )
}

export default AddToCollectionButton;
